var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import 'katex/dist/katex.min.css';
// Dynamically import KaTeX components to handle loading errors gracefully
var InlineMath = null;
var BlockMath = null;
try {
    var katexComponents = require('react-katex');
    InlineMath = katexComponents.InlineMath;
    BlockMath = katexComponents.BlockMath;
}
catch (error) {
    console.warn('KaTeX not available, math rendering disabled:', error);
}
/**
 * MathRenderer component that handles LaTeX math rendering
 * Automatically detects inline ($...$) and block ($$...$$) math expressions
 */
var MathRenderer = function (_a) {
    var children = _a.children, _b = _a.className, className = _b === void 0 ? '' : _b;
    // If KaTeX is not available, just return the text as-is
    if (!InlineMath || !BlockMath) {
        return _jsx("div", __assign({ className: "math-content ".concat(className) }, { children: children }));
    }
    var renderMath = function (text) {
        if (!text)
            return text;
        try {
            // Split text by block math first ($$...$$)
            var blockMathRegex = /\$\$(.*?)\$\$/g;
            var parts = [];
            var lastIndex = 0;
            var match = void 0;
            while ((match = blockMathRegex.exec(text)) !== null) {
                // Add text before the math
                if (match.index > lastIndex) {
                    var beforeText = text.slice(lastIndex, match.index);
                    parts.push(renderInlineMath(beforeText, parts.length));
                }
                // Add the block math
                try {
                    var mathContent = match[1].trim();
                    if (mathContent) {
                        parts.push(_jsx(BlockMath, { math: mathContent }, "block-".concat(parts.length)));
                    }
                    else {
                        parts.push(_jsx("span", __assign({ className: "text-muted-foreground" }, { children: "$$" }), "block-empty-".concat(parts.length)));
                    }
                }
                catch (error) {
                    console.warn('KaTeX block math rendering error:', error);
                    parts.push(_jsxs("span", __assign({ className: "text-red-500 font-mono text-sm" }, { children: ["$$", match[1], "$$"] }), "block-error-".concat(parts.length)));
                }
                lastIndex = match.index + match[0].length;
            }
            // Add remaining text
            if (lastIndex < text.length) {
                var remainingText = text.slice(lastIndex);
                parts.push(renderInlineMath(remainingText, parts.length));
            }
            return parts.length > 0 ? parts : renderInlineMath(text, 0);
        }
        catch (error) {
            console.warn('Math rendering error:', error);
            return text; // Fallback to plain text
        }
    };
    var renderInlineMath = function (text, keyOffset) {
        if (keyOffset === void 0) { keyOffset = 0; }
        if (!text)
            return text;
        try {
            // Split text by inline math ($...$, but not $$...$$)
            var inlineMathRegex = /(?<!\$)\$(?!\$)(.*?)(?<!\$)\$(?!\$)/g;
            var parts = [];
            var lastIndex = 0;
            var match = void 0;
            while ((match = inlineMathRegex.exec(text)) !== null) {
                // Add text before the math
                if (match.index > lastIndex) {
                    parts.push(text.slice(lastIndex, match.index));
                }
                // Add the inline math
                try {
                    var mathContent = match[1].trim();
                    if (mathContent) {
                        parts.push(_jsx(InlineMath, { math: mathContent }, "inline-".concat(keyOffset, "-").concat(parts.length)));
                    }
                    else {
                        parts.push(_jsx("span", __assign({ className: "text-muted-foreground" }, { children: "$$" }), "inline-empty-".concat(keyOffset, "-").concat(parts.length)));
                    }
                }
                catch (error) {
                    console.warn('KaTeX inline math rendering error:', error);
                    parts.push(_jsxs("span", __assign({ className: "text-red-500 font-mono text-sm" }, { children: ["$", match[1], "$"] }), "inline-error-".concat(keyOffset, "-").concat(parts.length)));
                }
                lastIndex = match.index + match[0].length;
            }
            // Add remaining text
            if (lastIndex < text.length) {
                parts.push(text.slice(lastIndex));
            }
            return parts.length > 1 ? parts : text;
        }
        catch (error) {
            console.warn('Inline math rendering error:', error);
            return text; // Fallback to plain text
        }
    };
    var renderedContent = renderMath(children);
    return (_jsx("div", __assign({ className: "math-content ".concat(className) }, { children: Array.isArray(renderedContent) ? (renderedContent.map(function (part, index) { return (_jsx("span", { children: part }, index)); })) : (renderedContent) })));
};
export default MathRenderer;
